import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Copy, Check, AlertCircle } from 'lucide-react';

// Guide box width constant - maps to Tailwind's max-w-lg (28rem = 448px)
// Adjust this if your Tailwind config changes
const GUIDE_BOX_WIDTH_PX = 28 * 16;

export const TutorialOverlay = ({ currentStep, maxStepReached, onAdvance, onGoBack, tutorialScript = [] }) => {
  const [targetData, setTargetData] = useState([]); // Array of { element, rect } to support multiple highlights
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogRect, setDialogRect] = useState(null);
  const [copiedText, setCopiedText] = useState(null);
  const [copyError, setCopyError] = useState(null);
  const step = tutorialScript[currentStep - 1]; // Steps are 1-indexed

  // Handle copy to clipboard with error handling
  const handleCopy = (text) => {
    // Defensive check for clipboard API availability
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      console.error('Clipboard API not available');
      setCopyError(text);
      setCopiedText(null);
      setTimeout(() => setCopyError(null), 3000);
      return;
    }

    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedText(text);
        setCopyError(null);
        setTimeout(() => setCopiedText(null), 2000);
      })
      .catch((err) => {
        console.error('Failed to copy to clipboard:', err);
        setCopyError(text);
        setCopiedText(null);
        // Clear error state after 3 seconds
        setTimeout(() => setCopyError(null), 3000);
      });
  };

  // Block clicks on non-allowed elements using document-level capture
  useEffect(() => {
    const handleClick = (e) => {
      // If no step (tutorial complete), allow all clicks
      if (!step) {
        return;
      }
      
      // Always allow clicks inside the tutorial instruction box
      const instructionBox = document.querySelector('[data-tutorial-instruction-box]');
      if (instructionBox && (instructionBox === e.target || instructionBox.contains(e.target))) {
        return;
      }

      // Always allow clicks on toasts (so user can dismiss them)
      const toast = e.target.closest('[data-radix-toast-viewport]') ||
                    e.target.closest('[data-state="open"][data-swipe-direction]') ||
                    e.target.closest('[toast-close]');
      if (toast) {
        return;
      }
      
      // Check if click is on any highlighted element or inside it
      // (use querySelectorAll to handle duplicate elements in responsive layouts)
      if (step?.highlight) {
        const highlightedEls = document.querySelectorAll(step.highlight);
        for (const highlightedEl of highlightedEls) {
          if (highlightedEl === e.target || highlightedEl.contains(e.target)) {
            return; // Allow click on highlighted element
          }
        }
      }
      
      // Check if click is on any allowed element
      if (step?.allowedElements) {
        for (const selector of step.allowedElements) {
          if (selector === '*') continue;
          try {
            const allowedEls = document.querySelectorAll(selector);
            for (const el of allowedEls) {
              if (el === e.target || el.contains(e.target)) {
                return; // Allow click
              }
            }
          } catch (err) {
            // Invalid selector, skip
          }
        }
      }
      
      // Block the click
      e.preventDefault();
      e.stopPropagation();
    };

    // Use capture phase to intercept clicks before they reach elements
    document.addEventListener('click', handleClick, true);
    
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [step]);

  // Calculate position of highlighted elements and check dialog state
  // Consolidated: single polling interval for both target positions and dialog detection
  useEffect(() => {
    const checkDialogOpen = () => {
      const dialog = document.querySelector('[role="dialog"]');
      setIsDialogOpen(!!dialog);

      if (dialog) {
        const rect = dialog.getBoundingClientRect();
        setDialogRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          right: rect.right,
          bottom: rect.bottom
        });
      } else {
        setDialogRect(null);
      }
    };

    const updateTargetPosition = () => {
      // First, update target positions (if we have a highlight)
      if (step?.highlight && typeof step.highlight === 'string') {
        try {
          // Find ALL matching visible elements and pair with their rects
          const elements = document.querySelectorAll(step.highlight);
          const visibleData = [];

          for (const el of elements) {
            const rect = el.getBoundingClientRect();
            // Element is visible if it has non-zero dimensions
            if (rect.width > 0 && rect.height > 0) {
              visibleData.push({
                element: el,
                rect: {
                  top: rect.top,
                  left: rect.left,
                  width: rect.width,
                  height: rect.height,
                  centerX: rect.left + rect.width / 2,
                  centerY: rect.top + rect.height / 2
                }
              });
            }
          }

          setTargetData(visibleData);
        } catch (err) {
          // Invalid selector - log and clear highlights
          console.warn('[TutorialOverlay] Invalid highlight selector:', step.highlight, err);
          setTargetData([]);
        }
      } else {
        setTargetData([]);
      }

      // Then, check dialog state (consolidated - no separate interval)
      checkDialogOpen();
    };

    // Initial calculation with delay for DOM to settle
    const timer = setTimeout(updateTargetPosition, 100);

    // Recalculate on window resize or scroll
    window.addEventListener('resize', updateTargetPosition);
    window.addEventListener('scroll', updateTargetPosition);

    // Single consolidated interval for both target positions and dialog state (500ms)
    const interval = setInterval(updateTargetPosition, 500);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener('resize', updateTargetPosition);
      window.removeEventListener('scroll', updateTargetPosition);
    };
  }, [step, currentStep]);

  // Filter highlights - when a modal is open, only show highlights inside the modal
  // This hook MUST be called before any early returns to comply with Rules of Hooks
  const visibleHighlights = React.useMemo(() => {
    // Handle null/undefined step or no data
    if (!step || !step.highlight || targetData.length === 0) {
      return targetData.map(d => d.rect);
    }

    // No modal open - return all highlight rects
    if (!isDialogOpen) {
      return targetData.map(d => d.rect);
    }

    // Modal is open - filter to only elements inside the modal
    try {
      const dialog = document.querySelector('[role="dialog"]');
      if (!dialog) return targetData.map(d => d.rect);

      // Filter using the paired elements (fixes index mismatch bug)
      const filtered = targetData.filter(data => dialog.contains(data.element));
      return filtered.map(d => d.rect);
    } catch (err) {
      // On error, show all highlights
      return targetData.map(d => d.rect);
    }
  }, [isDialogOpen, targetData, step?.highlight]);

  // Early return after all hooks have been called
  if (!step) return null;

  // Calculate max step with safe fallback (prevents -Infinity and division by zero)
  // Ensures maxStep is at least 1 even if all steps are 0/undefined
  const maxStep = Math.max(1,
    tutorialScript.length > 0
      ? Math.max(...tutorialScript.map(s => s.step || 0))
      : 0
  );

  // Calculate arrow position - points from instruction box to first visible target
  const getArrowStyle = () => {
    const firstTarget = visibleHighlights[0];
    if (!firstTarget || !step.showArrow) return null;

    // Target is in bottom action bar area
    const isTargetBelow = firstTarget.top > window.innerHeight / 2;

    return {
      position: 'fixed',
      left: firstTarget.centerX,
      top: isTargetBelow ? firstTarget.top - 60 : firstTarget.top + firstTarget.height + 10,
      transform: 'translateX(-50%)',
      zIndex: 10001
    };
  };

  const arrowStyle = getArrowStyle();

  // Calculate guide box position - left side of modal when modal is open
  const getGuideBoxStyle = () => {
    if (!isDialogOpen || !dialogRect) {
      // No modal open - use default positioning
      return {
        className: `fixed left-1/2 transform -translate-x-1/2 max-w-lg pointer-events-auto ${
          step.positionBottom ? 'bottom-28' : 'top-16'
        }`,
        style: { zIndex: 10002 }
      };
    }

    // Modal is open - position guide box to the left of the modal
    const spaceOnLeft = dialogRect.left;
    const spaceOnRight = window.innerWidth - dialogRect.right;

    // Check if there's enough space on the left
    if (spaceOnLeft >= GUIDE_BOX_WIDTH_PX + 32) {
      // Position on the left side of the modal
      return {
        className: 'fixed max-w-lg pointer-events-auto',
        style: {
          right: `${window.innerWidth - dialogRect.left + 16}px`,
          top: `${Math.max(16, dialogRect.top)}px`,
          maxHeight: `${dialogRect.height}px`,
          overflowY: 'auto',
          zIndex: 10002
        }
      };
    } else if (spaceOnRight >= GUIDE_BOX_WIDTH_PX + 32) {
      // Position on the right side of the modal
      return {
        className: 'fixed max-w-lg pointer-events-auto',
        style: {
          left: `${dialogRect.right + 16}px`,
          top: `${Math.max(16, dialogRect.top)}px`,
          maxHeight: `${dialogRect.height}px`,
          overflowY: 'auto',
          zIndex: 10002
        }
      };
    } else {
      // Not enough space on either side - position at top on mobile
      return {
        className: 'fixed left-1/2 transform -translate-x-1/2 max-w-[calc(100vw-2rem)] pointer-events-auto top-4',
        style: { zIndex: 10002 }
      };
    }
  };

  const guideBoxPosition = getGuideBoxStyle();

  return (
    <div className="tutorial-overlay pointer-events-none">
      {/* Spotlight borders on target elements - render one for EACH target */}
      {visibleHighlights.length > 0 && step.highlight && visibleHighlights.map((targetRect, index) => (
        <div
          key={`highlight-${index}`}
          className="fixed border-4 border-amber-400 rounded-lg pointer-events-none"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow: '0 0 30px rgba(251, 191, 36, 0.8), 0 0 60px rgba(251, 191, 36, 0.4)',
            zIndex: 10000,
            animation: 'pulse 2s infinite'
          }}
        />
      ))}

      {/* Arrow pointing to target - always show, z-index above dialogs */}
      {arrowStyle && visibleHighlights[0] && (
        <div
          className="text-amber-400 text-5xl animate-bounce pointer-events-none"
          style={arrowStyle}
        >
          {visibleHighlights[0].top > window.innerHeight / 2 ? '↓' : '↑'}
        </div>
      )}

      {/* Instruction box - positioned to the left of modals when open */}
      <div
        className={guideBoxPosition.className}
        style={guideBoxPosition.style}
        data-tutorial-instruction-box
      >
          <div className="bg-stone-900/95 border-2 border-amber-500 rounded-lg p-6 shadow-2xl backdrop-blur-sm">
            {/* Header with step counter */}
            <div className="mb-4">
              <h3 className="text-xl font-bold text-amber-400">
                Step {step?.step || currentStep} of {maxStep}
              </h3>
            </div>

            <h4 className="text-lg font-semibold text-stone-100 mb-2 select-text">
              {step.title}
            </h4>

            <div className="text-stone-300 mb-4 select-text space-y-3">
              {/* Parse instruction text for bold (**text**) and copyable ({{text}}) syntax */}
              {step.instruction.split(/(\{\{[^}]+\}\}|\*\*[^*]+\*\*)/).map((part, i) => {
                // Copyable code block {{text}}
                if (part.startsWith('{{') && part.endsWith('}}')) {
                  const text = part.slice(2, -2);
                  const isCopied = copiedText === text;
                  const hasError = copyError === text;
                  return (
                    <div key={i} className="my-2 bg-black/50 border border-amber-600/30 rounded p-3 flex items-center gap-2">
                      <code className="flex-1 text-amber-300 font-mono text-sm break-all select-all">
                        {text}
                      </code>
                      <button
                        onClick={() => handleCopy(text)}
                        className={`px-2 py-1 border rounded transition-colors flex items-center gap-1 text-xs ${
                          hasError
                            ? 'bg-red-600/20 hover:bg-red-600/40 border-red-500/50 text-red-400'
                            : 'bg-amber-600/20 hover:bg-amber-600/40 border-amber-500/50 text-amber-400'
                        }`}
                        title={hasError ? 'Copy failed - try selecting and copying manually' : 'Copy to clipboard'}
                      >
                        {isCopied ? (
                          <>
                            <Check className="w-3 h-3" />
                            <span>Copied!</span>
                          </>
                        ) : hasError ? (
                          <>
                            <AlertCircle className="w-3 h-3" />
                            <span>Failed</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                  );
                }
                // Bold text **text**
                if (part.startsWith('**') && part.endsWith('**')) {
                  return <strong key={i} className="text-amber-400 font-bold">{part.slice(2, -2)}</strong>;
                }
                // Regular text
                return <span key={i}>{part}</span>;
              })}
            </div>

            {step.requiresNext && (
              <button
                onClick={onAdvance}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 px-4 rounded transition-colors ring-4 ring-amber-400 animate-pulse text-lg"
                data-tutorial="next-button"
              >
                {step.nextButtonText || 'Next'}
              </button>
            )}

            {/* Progress bar */}
            <div className="mt-4 bg-stone-700 rounded-full h-2">
              <div
                className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((step?.step || currentStep) / maxStep) * 100}%` }}
              />
            </div>

            {/* Step Navigation */}
            <div className="mt-4 flex items-center justify-between border-t border-stone-700 pt-4">
              {/* Back button */}
              <button
                onClick={onGoBack}
                disabled={currentStep <= 1}
                className={`flex items-center gap-1 px-3 py-1.5 rounded border transition-colors ${
                  currentStep > 1
                    ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/20 cursor-pointer'
                    : 'border-stone-700 text-stone-600 cursor-not-allowed'
                }`}
                title="Go back to previous step"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back</span>
              </button>

              {/* Forward button - only available if we've already completed future steps */}
              <button
                onClick={onAdvance}
                disabled={currentStep >= maxStepReached}
                className={`flex items-center gap-1 px-3 py-1.5 rounded border transition-colors ${
                  currentStep < maxStepReached
                    ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/20 cursor-pointer'
                    : 'border-stone-700 text-stone-600 cursor-not-allowed'
                }`}
                title={currentStep < maxStepReached ? 'Return to next step' : 'Complete this step to continue'}
              >
                <span className="text-sm font-medium">Forward</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
    </div>
  );
};
